import React, { useEffect, useState } from "react";
import { BellOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { MessageSquare } from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { UserDropdown } from "../ui/dropdown";
import { useTheme } from "../../contexts/ThemeContext";
import Search from "../Search/Search";
import useStore from "../../store/useStore";
import AlertTray from "../Notifications/AlertTray";
import { NotificationService } from "../../services/NotificationService";
import {
  Notification,
  NotificationStatus,
  NotificationType,
} from "../../model_gen/notification";
import { useWebSocket } from "../../utils/Websockets";
import { cn } from "../../lib/utils";

interface HeaderComponentProps {
  showAssistant?: boolean;
  assistantOpen?: boolean;
  onToggleAssistant?: () => void;
}

const HeaderComponent: React.FC<HeaderComponentProps> = ({
  showAssistant = false,
  assistantOpen = false,
  onToggleAssistant,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const { dataReadyTutorial } = useStore();
  const [company, setCompany] = useState<Company | null>(null);
  const { user, logout, loginWithRedirect } = useAuth0();
  const { theme, toggleTheme } = useTheme();
  const [alertTrayVisible, setAlertTrayVisible] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  useEffect(() => {
    if (apiKey) {
      CompanyService.getCompany(null, apiKey).subscribe(
        (result: Company) => {
          setCompany(result);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [apiKey]);

  useEffect(() => {
    if (apiKey) {
      // Fetch unread notifications
      fetchUnreadNotifications();
    }
  }, [apiKey, refreshTrigger]);

  const fetchUnreadNotifications = () => {
    // Only get CREATED status notifications (unread)
    const notificationStatuses: NotificationStatus[] = ["CREATED"];
    // Only include FINETUNING_COMPLETE notifications, exclude FAULT
    const excludeTypes: NotificationType[] = ["FAULT"];
    NotificationService.getNotifications(
      notificationStatuses,
      [],
      excludeTypes,
      null,
      apiKey
    ).subscribe(
      (result: Notification[]) => {
        setUnreadCount(result ? result.length : 0);
      },
      (error: any) => {
        console.error("Error fetching notification count:", error);
      }
    );
  };

  // Handle new notification from websocket
  const handleNewNotification = () => {
    // A new notification arrived via websocket, trigger a refresh
    setRefreshTrigger((prev) => prev + 1);
  };

  // Set up websocket listener for notifications
  useWebSocket({
    key: company?.id,
    eventType: "NOTIFICATION",
    onMessage: handleNewNotification,
  });

  const toggleAlertTray = () => {
    setAlertTrayVisible(!alertTrayVisible);
    if (alertTrayVisible) {
      fetchUnreadNotifications();
    }
  };

  const handleLogout = () => {
    // Clear all cached authentication data
    localStorage.removeItem("auth0_org_id");
    localStorage.removeItem("selectedCompanyId");
    localStorage.removeItem("auth0_access_token");
    localStorage.removeItem("auth0_return_path");
    
    // Clear all Auth0 cache keys
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("@@auth0spajs@@")) {
        localStorage.removeItem(key);
      }
    });
    
    logout({ logoutParams: { returnTo: `${window.location.origin}` } });
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleSwitchOrganization = () => {
    // Save current path to return to after switching
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath !== "/" && currentPath !== "/login") {
      localStorage.setItem("auth0_return_path", currentPath);
    }
    
    // Clear all cached authentication data
    localStorage.removeItem("auth0_org_id");
    localStorage.removeItem("selectedCompanyId");
    localStorage.removeItem("auth0_access_token");
    
    // Set flag to indicate we're switching organizations
    localStorage.setItem("switching_org", "true");
    
    // Clear all Auth0 cache keys (forces fresh authentication)
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("@@auth0spajs@@")) {
        localStorage.removeItem(key);
      }
    });
    
    // Force a full page reload to clear all React state and fetch new token
    window.location.href = "/";
  };

  return (
    <div className="flex items-center justify-between w-full h-16 px-6">
      {/* Left Section - Search */}
      <div className="flex items-center flex-1 max-w-2xl mr-6">
        <Search disabled={!dataReadyTutorial} />
      </div>

      {/* Right Section - Action buttons and user menu */}
      <div className="flex items-center gap-4 flex-shrink-0">
        {/* Documentation Button */}
        <Button
          variant="ghost"
          size="icon"
          asChild
          className="text-muted-foreground hover:text-foreground">
          <a
            href="https://docs.trymaitai.ai"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Documentation">
            <QuestionCircleOutlined className="w-5 h-5" />
          </a>
        </Button>

        {/* Assistant Toggle - Only show on agent pages */}
        {showAssistant && onToggleAssistant && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleAssistant}
            className={cn(
              "text-muted-foreground hover:text-foreground",
              assistantOpen && "text-maitai-green hover:text-maitai-green"
            )}
            aria-label="Toggle Assistant"
            title={assistantOpen ? "Close Assistant" : "Open Assistant"}>
            <MessageSquare
              className={cn("w-5 h-5", assistantOpen && "fill-current")}
            />
          </Button>
        )}

        {/* Notifications Button */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleAlertTray}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Notifications">
            <BellOutlined className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* User Dropdown */}
        <UserDropdown
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          onSwitchOrganization={handleSwitchOrganization}
          theme={theme}
          onToggleTheme={toggleTheme}
          apiKey={apiKey}
          showCredits={!!company?.usage_based_billing}
        />
      </div>

      {/* Notifications Drawer */}
      <AlertTray
        visible={alertTrayVisible}
        onClose={() => setAlertTrayVisible(false)}
        refreshTrigger={refreshTrigger}
        onNotificationsRead={fetchUnreadNotifications}
      />
    </div>
  );
};

export default HeaderComponent;
